import * as React from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import schemas from 'modules/custom_resources/store/collections/customResources/schemas'
import values from 'modules/custom_resources/store/collections/customResources/values'
import type Field from 'modules/custom_resources/store/models/customResources/Field'
import type Schema from 'modules/custom_resources/store/models/customResources/Schema'
import type Value from 'modules/custom_resources/store/models/customResources/Value'
import { getValue } from 'modules/employees/components/CustomResources/SchemaComponent/utils/getValue'
import { RouteContext } from 'modules/employees/pages/CustomTablesListPage/context'

interface CustomFieldValueProps {
  schema: Schema | undefined
  field: Field
  value?: Value
  companyLocale: string
}

function CustomTableValue({ schema, field, value, companyLocale }: CustomFieldValueProps) {
  return (
    <Box flexDirection="row" gap="s8">
      <Text.Normal weight="medium">{`${field.attributes.label}: `}</Text.Normal>
      <Text.Normal>
        {getValue({
          schema,
          field,
          currentValue: value,
          notAvailable: '',
          companyLocale,
        })}
      </Text.Normal>
    </Box>
  )
}

export default function DeleteCustomTablePage() {
  const { schemaId, valueId } = useParams()
  const { fields } = useOutletContext<{ fields: Field[] }>()
  const rootRoute = React.useContext(RouteContext)
  const schema = schemas.get(Number(schemaId))

  const schemaFields = fields.filter((f) => f.get('schema_id') === schema?.attributes.id)

  const value = values.get(Number(valueId))
  const navigate = useNavigate()
  const {
    company: { locale: companyLocale },
  } = useCurrentSession()

  const onClose = () => {
    !!rootRoute && navigate(rootRoute.customTablesIndex())
  }

  const { performAction } = useAsyncAction(async (closePortal: ClosePortalFn) => {
    await value?.destroy()

    closePortal()
  })

  return (
    <Modal
      illustration="delete"
      title={i18n.t('custom_tables.delete_modal.title')}
      description={i18n.t('custom_tables.delete_modal.description')}
      onSubmit={performAction}
      onClose={onClose}
      footer={
        <Button type="negative" submit>
          {i18n.t('custom_tables.delete_modal.submit')}
        </Button>
      }
    >
      {schemaFields.map((field, key) => (
        <CustomTableValue
          key={key}
          schema={schema}
          field={field}
          value={value}
          companyLocale={companyLocale || ''}
        />
      ))}
    </Modal>
  )
}
